import { useCallback } from "react"

/*
 * How a sideways row fades its edges. `useEdgeFade` marks a toolbar row's ends on the DOM;
 * `edgeFadeOf` is the StatusSwitcher's rule, kept apart from the row so it can be checked without a
 * browser: an edge with more of the row past it fades, only as wide as what is past it, up to
 * `FADE_WIDTH`, so a row stopped a few pixels along fades those pixels, not all that stands there.
 */

/**
 * A ref for a sideways scroller that marks which of its ends has more to scroll to, so it can fade
 * there: `data-overflow` while more is past its right edge, `data-overflow-start` once it has
 * scrolled from its left. Each is "true" or "false", read on every scroll, on a resize of the
 * scroller or of what it holds, and when what it holds changes; set on the DOM rather than kept in
 * state, so measuring never renders again. `fade-x-end` (the Board) reads the first,
 * `fade-x-edges` (a toolbar on `compact`) both.
 */
export function useEdgeFade<Scroller extends HTMLElement = HTMLDivElement>() {
  return useCallback((scroller: Scroller | null) => {
    if (!scroller) return
    const measure = () => {
      scroller.dataset.overflowStart = String(scroller.scrollLeft > 1)
      scroller.dataset.overflow = String(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
      )
    }
    measure()
    const resized = new ResizeObserver(measure)
    const observe = () => {
      resized.disconnect()
      resized.observe(scroller)
      for (const child of scroller.children) resized.observe(child)
    }
    observe()
    const changed = new MutationObserver(() => {
      observe()
      measure()
    })
    changed.observe(scroller, { childList: true })
    scroller.addEventListener("scroll", measure, { passive: true })
    return () => {
      resized.disconnect()
      changed.disconnect()
      scroller.removeEventListener("scroll", measure)
    }
  }, [])
}

/** The widest an edge's fade grows, in px: the `fade-x-*` utilities' width in `tokens.css`. */
export const FADE_WIDTH = 24

/** Which edges fade (the row's `data-fade`), and how wide each fade is, in px. */
export type EdgeFade = { fade: "none" | "start" | "end" | "both"; start: number; end: number }

/** A row's edge fades from how far it has scrolled, how wide it shows and how wide it runs. */
export function edgeFadeOf({
  scrollLeft,
  clientWidth,
  scrollWidth,
}: {
  scrollLeft: number
  clientWidth: number
  scrollWidth: number
}): EdgeFade {
  const widthOf = (past: number) => (past > 1 ? Math.min(past, FADE_WIDTH) : 0)
  const start = widthOf(scrollLeft)
  const end = widthOf(scrollWidth - clientWidth - scrollLeft)
  const fade = start && end ? "both" : start ? "start" : end ? "end" : "none"
  return { fade, start, end }
}
